import { useState } from 'react';
import { keepPreviousData, useQuery, type QueryKey } from '@tanstack/react-query';
import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import type { PageFetcher, ListParams } from '@/api/pagedList';
import { MAX_PAGE_SIZE } from '@/lib/apiClient';
import { useCurrentSchool } from './useCurrentSchool';
import { usePageSizes } from './usePageSizes';

export interface ServerTableControl {
  search: string;
  onSearchChange: (value: string) => void;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  filters: Record<string, string>;
  onFilterChange: (field: string, value: string) => void;
  rowCount: number;
}

/** Isolated per-table state. Search is applied on submit; every query includes the complete list scope. */
export function useServerList<T>(fetch: PageFetcher<T>, key: QueryKey, params: ListParams = {}, options: {
  schoolId?: number;
  organizationWide?: boolean;
  enabled?: boolean;
  aliases?: Record<string, string>;
  /** Toolbar filters (after aliasing) the endpoint takes as query parameters of their own, not list fields. */
  paramFilters?: string[];
  /** The order before a column header is clicked, shown on that column. */
  defaultSort?: GridSortModel;
  /** Exact field values the list is always narrowed to, sent with the toolbar's filters (e.g. one tab's stage). */
  fixedFilters?: Record<string, string>;
} = {}) {
  const current = useCurrentSchool();
  const schoolId = options.schoolId ?? current.currentSchoolId;
  // Global Settings › Page Size List: its first size is the default.
  const defaultPageSize = usePageSizes()[0] ?? 10;
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: defaultPageSize });
  const [sortModel, setSortModel] = useState<GridSortModel>(options.defaultSort ?? []);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const scope = JSON.stringify([options.organizationWide ? null : schoolId, params]);
  const [previousScope, setPreviousScope] = useState(scope);
  if (previousScope !== scope) {
    setPreviousScope(scope);
    setPagination({ ...pagination, page: 0 });
  }
  // The search box submits on Enter, so a new term is fetched at once from the first page.
  const onSearchChange = (value: string) => {
    if (value.trim() === search) return;
    setSearch(value.trim());
    setPagination((model) => ({ ...model, page: 0 }));
  };
  const field = (name: string) => options.aliases?.[name] ?? name;
  const sort = sortModel[0];
  const chosen = Object.entries(filters).filter(([, value]) => value !== '').map(([name, value]): [string, string] => [field(name), field(name) === 'isActive' && (value === 'Active' || value === 'Inactive') ? String(value === 'Active') : value]);
  const isParam = ([name]: [string, string]) => options.paramFilters?.includes(name) ?? false;
  const activeFilters = [...Object.entries(options.fixedFilters ?? {}), ...chosen.filter((entry) => !isParam(entry))];
  // What the list is narrowed to, whatever page is shown.
  const listParams = {
    ...params,
    ...Object.fromEntries(chosen.filter(isParam)),
    search: search || undefined,
    sortBy: sort?.sort ? field(sort.field) : undefined,
    sortDescending: sort?.sort ? sort.sort === 'desc' : undefined,
    filters: activeFilters.length ? JSON.stringify(Object.fromEntries(activeFilters)) : undefined,
  };
  const request = { ...listParams, page: pagination.page + 1, pageSize: pagination.pageSize };
  const query = useQuery({
    queryKey: [...key, 'table', options.organizationWide ? null : schoolId, request],
    queryFn: ({ signal }) => fetch(schoolId ?? 0, request, signal),
    enabled: options.enabled !== false && (options.organizationWide || schoolId !== null),
    // Keep the current rows on screen while the next page, sort or search loads.
    placeholderData: keepPreviousData,
  });
  // Keep the last count while a page loads, otherwise DataGrid would reset a later page to zero.
  const [rowCount, setRowCount] = useState(0);
  if (query.data && rowCount !== query.data.totalCount) setRowCount(query.data.totalCount);
  if (query.data && !query.isPlaceholderData && pagination.page > 0 && pagination.page * pagination.pageSize >= query.data.totalCount) {
    setPagination({ ...pagination, page: Math.max(0, Math.ceil(query.data.totalCount / pagination.pageSize) - 1) });
  }
  const table: ServerTableControl = {
    search,
    onSearchChange,
    paginationModel: pagination,
    onPaginationModelChange: setPagination,
    sortModel,
    onSortModelChange: (model) => { setSortModel(model); setPagination((value) => ({ ...value, page: 0 })); },
    filters,
    onFilterChange: (name, value) => { setFilters((existing) => ({ ...existing, [name]: value })); setPagination((model) => ({ ...model, page: 0 })); },
    rowCount,
  };
  return {
    ...query,
    data: query.data?.items,
    totalCount: query.data?.totalCount ?? rowCount,
    // First load or a page/sort/search transition; a background refetch after a save keeps the rows as they are.
    isLoading: query.isLoading || (query.isPlaceholderData && query.isFetching) || (!options.organizationWide && schoolId === null && current.isLoading),
    error: query.error ?? (!options.organizationWide && schoolId === null ? current.error : null),
    refetch: async () => { const result = await query.refetch(); return { ...result, data: result.data?.items }; },
    /** Every row the search and filters leave, across all pages, in the list's order (e.g. for an export). */
    fetchAll: async (signal?: AbortSignal) => {
      const items: T[] = [];
      for (let page = 1; ; page++) {
        const result = await fetch(schoolId ?? 0, { ...listParams, page, pageSize: MAX_PAGE_SIZE }, signal);
        items.push(...result.items);
        if (result.items.length === 0 || items.length >= result.totalCount) return items;
      }
    },
    table,
  };
}
